/*
  assets/gtender-free-bookmarks.js — GTENDER-STAR-HEADER-OWNER-13
  Owner: direct Visitor/Free star-unstar, local persistence, Header Star count/list, and Star List sharing.
  Boundary: starring never requires sign-in. Visitor stars are device-local; authenticated Free stars remain
  local to this device and are isolated by native LittleG subject until the later cloud-sync milestone.
  Public result sharing remains owned by assets/gtender-public-entry.js; this runtime only projects that
  existing share control into the visible card header and owns sharing inside the Star List it renders.
*/
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-gtender-public-entry]';
  var VISITOR_STAR_KEY = 'gtender.visitor.stars.v1';
  var ACCOUNT_STAR_KEY_PREFIX = 'gtender.free.stars.v2:';
  var LEGACY_BOOKMARK_KEY = 'gtender.free.bookmarks.v1';
  var SUBJECT_RE = /^littleg_[a-f0-9]{40}$/;
  var INTERNAL_TENDER_ID_RE = /^gtender(?:-[a-z0-9]+){2,}$/i;
  var SHARED_TENDER_ID_RE = /^[A-Za-z0-9._:-]{3,240}$/;
  var TRUSTED_OFFICIAL_DOMAINS = [
    'gov.hk',
    'ha.org.hk',
    'hkairport.com',
    'mtr.com.hk',
    'westkowloon.hk',
    'hksp.org',
    'cyberport.hk',
    'hkp.com.hk',
    'w5.ab.ust.hk',
    'etender.hkmu.edu.hk',
  ];
  var roots = new Set();
  var starDialogOpenerByRoot = new WeakMap();

  function clean(value) {
    return String(value == null ? '' : value).trim();
  }
  function clipped(value, max) {
    return clean(value).slice(0, max || 500);
  }

  function locale() {
    var lang = clean(document.documentElement.lang) || navigator.language || '';
    return lang.toLowerCase().indexOf('zh') === 0 ? 'zh' : 'en';
  }
  var COPY = {
    zh: {
      star: '儲存機會',
      unstar: '取消儲存機會',
      starred: '已收藏招標',
      list: '已收藏招標',
      saved: '已收藏',
      empty: '沒有已收藏的招標',
      emptyHelp: '點擊招標卡片上的收藏圖標，即可新增至此裝置的已收藏清單。',
      share: '分享',
      copied: '已複製連結',
      copyLink: '複製連結',
      shareLead: '我在 GTender 找到了這個招標，想與你分享：',
      official: '官方來源',
      close: '關閉已收藏招標',
      visitor: '此裝置',
      account: '此帳戶的本機清單',
      unavailable: '暫時無法載入招標詳情',
      unavailableHelp: '這筆已收藏的招標目前無法與公開資料配對。請稍後再試，或移除收藏。',
      storageUnavailable: '暫時無法讀取已收藏招標',
      storageUnavailableHelp:
        '此裝置的已收藏資料無法安全讀取。請重設目前清單；這不會建立、修改或刪除任何雲端收藏。',
      resetList: '重設此裝置清單',
    },
    en: {
      star: 'Save Opportunity',
      unstar: 'Remove Saved Opportunity',
      starred: 'Saved tenders',
      list: 'Saved tenders',
      saved: 'Saved',
      empty: 'No saved tenders',
      emptyHelp:
        'Select the bookmark on any tender card to add it to the saved list on this device.',
      share: 'Share',
      copied: 'Link copied',
      copyLink: 'Copy link',
      shareLead: 'I found this tender on GTender and thought it might be useful:',
      official: 'Official source',
      close: 'Close saved tenders',
      visitor: 'This device',
      account: 'Local list for this account',
      unavailable: 'Tender details temporarily unavailable',
      unavailableHelp:
        'This saved tender could not be matched to the current public feed. Try again later or remove the saved item.',
      storageUnavailable: 'Saved tenders could not be read',
      storageUnavailableHelp:
        'The saved-tender data on this device could not be read safely. You can reset this local list; no cloud-saved tender will be created, changed, or deleted.',
      resetList: 'Reset this device list',
    },
  };

  function t(key) {
    return (COPY[locale()] && COPY[locale()][key]) || COPY.en[key] || '';
  }

  function sessionScope() {
    var session = window.LittleGWebSession;
    var state = session && typeof session.getState === 'function' ? session.getState() : null;
    var subject = clean(state && state.profile && (state.profile.id || state.profile.subject));
    if (state && state.status === 'authenticated' && SUBJECT_RE.test(subject)) {
      return {
        authenticated: true,
        subject: subject,
        key: ACCOUNT_STAR_KEY_PREFIX + subject,
        scope: 'native-free-local',
      };
    }
    return { authenticated: false, subject: '', key: VISITOR_STAR_KEY, scope: 'visitor-local' };
  }

  function safeUrl(value) {
    var raw = clean(value);
    if (!raw) return '';
    try {
      var url = new URL(raw, window.location.origin);
      if (url.protocol !== 'https:') return '';
      var host = url.hostname.toLowerCase();
      var trusted = TRUSTED_OFFICIAL_DOMAINS.some(function (domain) {
        return host === domain || host.endsWith('.' + domain);
      });
      return trusted ? url.toString() : '';
    } catch (_error) {
      return '';
    }
  }

  function gtenderTenderUrl(id) {
    var key = clean(id);
    if (!SHARED_TENDER_ID_RE.test(key)) return '';
    try {
      var url = new URL(window.location.href);
      url.search = '';
      url.hash = '';
      url.searchParams.set('tender', key);
      return url.toString();
    } catch (_error) {
      return '';
    }
  }

  function shareMessage(title) {
    return t('shareLead') + '\n' + clean(title);
  }

  function shareClipboardText(title, url) {
    return shareMessage(title) + '\n' + url;
  }

  function sanitizeRecord(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
    var id = clipped(value.id, 240);
    if (!id) return null;
    return {
      id: id,
      title: clipped(value.title, 500),
      department: clipped(value.department, 240),
      deadline: clipped(value.deadline, 160),
      source: clipped(value.source, 240),
      officialUrl: safeUrl(value.officialUrl),
      starredAt: /^\d{4}-\d{2}-\d{2}T/.test(clean(value.starredAt))
        ? clean(value.starredAt)
        : new Date().toISOString(),
    };
  }

  function displayableTitle(record) {
    var title = clipped(record && record.title, 500);
    var id = clipped(record && record.id, 240);
    if (!title) return '';
    if (id && title.toLowerCase() === id.toLowerCase()) return '';
    if (INTERNAL_TENDER_ID_RE.test(title)) return '';
    return title;
  }

  function readStars(scope) {
    scope.readFailed = false;

    try {
      const raw = window.localStorage.getItem(scope.key);
      const parsed = raw === null ? [] : JSON.parse(raw);

      if (!Array.isArray(parsed)) {
        throw new Error('Invalid bookmark storage');
      }

      const seen = new Set();
      return parsed.map(sanitizeRecord).filter(function (record) {
        if (!record || seen.has(record.id)) return false;
        seen.add(record.id);
        return true;
      });
    } catch (_error) {
      scope.readFailed = true;
      return [];
    }
  }

  function writeStars(scope, records) {
    if (scope.readFailed) return false;

    try {
      var safe = (Array.isArray(records) ? records : []).map(sanitizeRecord).filter(Boolean);
      window.localStorage.setItem(scope.key, JSON.stringify(safe));
      return true;
    } catch (_error) {
      return false;
    }
  }

  function cloneSharedIcon(name, owner) {
    const root = owner && owner.closest ? owner.closest(ROOT_SELECTOR) : null;
    const selector = 'template[data-gtender-icon-template="' + name + '"]';
    const template = (root || document).querySelector(selector);
    const source = template && template.content ? template.content.querySelector('svg') : null;
    return source ? source.cloneNode(true) : null;
  }

  function starIcon(filled) {
    const svg = cloneSharedIcon('star');
    if (!svg) return '';

    svg.setAttribute('fill', filled ? 'currentColor' : 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.2');
    svg.setAttribute('stroke-linejoin', 'round');
    return svg.outerHTML;
  }

  function findCard(root, id, sourceElement) {
    var card =
      sourceElement && sourceElement.closest
        ? sourceElement.closest('.gtender-partner-entry__card')
        : null;
    if (card) return card;
    var triggers = root.querySelectorAll('[data-gtender-detail-open]');
    for (var i = 0; i < triggers.length; i += 1) {
      if (clean(triggers[i].getAttribute('data-gtender-detail-open')) === id) {
        return triggers[i].closest ? triggers[i].closest('.gtender-partner-entry__card') : null;
      }
    }
    return null;
  }

  function recordFromCard(root, id, sourceElement) {
    var card = findCard(root, id, sourceElement);
    if (!card) return null;
    var official = card.querySelector
      ? card.querySelector('.gtender-card__action-row a[href]')
      : null;
    var department = card.querySelector
      ? card.querySelector('.gtender-card__badge--department')
      : null;
    var deadline = card.querySelector ? card.querySelector('.gtender-card__date') : null;
    var source = card.querySelector ? card.querySelector('.gtender-public-record-meta') : null;
    var title = card.querySelector ? card.querySelector('.gtender-card__title') : null;
    return sanitizeRecord({
      id: id,
      title: title ? clean(title.textContent) : '',
      department: department ? clean(department.title || department.textContent) : '',
      deadline: deadline ? clean(deadline.textContent) : '',
      source: source ? clean(source.textContent) : '',
      officialUrl: official ? official.href : '',
      starredAt: new Date().toISOString(),
    });
  }

  function mergeHydratedRecord(record, candidate) {
    var current = sanitizeRecord(record);
    var incoming = sanitizeRecord(candidate);
    if (!current || !incoming || current.id !== incoming.id) return current;
    return sanitizeRecord({
      id: current.id,
      title: displayableTitle(current) || displayableTitle(incoming),
      department: current.department || incoming.department,
      deadline: current.deadline || incoming.deadline,
      source: current.source || incoming.source,
      officialUrl: current.officialUrl || incoming.officialUrl,
      starredAt: current.starredAt || incoming.starredAt,
    });
  }

  function recordsDiffer(a, b) {
    if (!a || !b) return true;
    return (
      a.id !== b.id ||
      a.title !== b.title ||
      a.department !== b.department ||
      a.deadline !== b.deadline ||
      a.source !== b.source ||
      a.officialUrl !== b.officialUrl ||
      a.starredAt !== b.starredAt
    );
  }

  function hydrateVisibleRecords(root, scope, records) {
    var changed = false;
    var next = (Array.isArray(records) ? records : []).map(function (record) {
      var candidate = recordFromCard(root, record.id, null);
      if (!candidate) return record;
      var merged = mergeHydratedRecord(record, candidate);
      if (recordsDiffer(record, merged)) changed = true;
      return merged;
    });
    if (changed) writeStars(scope, next);
    return next;
  }

  function setButtonState(button, saved) {
    if (!button) return;
    button.setAttribute('aria-pressed', saved ? 'true' : 'false');
    button.setAttribute('aria-label', saved ? t('unstar') : t('star'));
    button.title = saved ? t('unstar') : t('star');
    button.removeAttribute('data-gtender-native-auth-open');
    button.innerHTML = starIcon(saved);
  }

  function headerControl() {
    return document.querySelector('[data-gtender-header-bookmarks]');
  }

  function reducedMotion() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function pulseHeader() {
    var header = headerControl();
    if (!header || reducedMotion() || typeof header.animate !== 'function') return;
    header.animate(
      [{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }],
      { duration: 220, easing: 'ease-out' }
    );
  }

  function animateStarToHeader(source) {
    var header = headerControl();
    var target = header && (header.querySelector('[data-gtender-header-star]') || header);
    if (!source || !target || reducedMotion()) {
      pulseHeader();
      return;
    }
    var start = source.getBoundingClientRect();
    var end = target.getBoundingClientRect();
    if (!start.width || !end.width) {
      pulseHeader();
      return;
    }

    var flyer = document.createElement('span');
    flyer.setAttribute('aria-hidden', 'true');
    flyer.innerHTML = starIcon(true);
    flyer.style.cssText =
      'position:fixed;left:' +
      (start.left + start.width / 2 - 10) +
      'px;top:' +
      (start.top + start.height / 2 - 10) +
      'px;width:20px;height:20px;display:grid;place-items:center;' +
      'pointer-events:none;z-index:2147483000;color:#64748b;will-change:transform,opacity;';
    document.body.appendChild(flyer);

    var dx = end.left + end.width / 2 - (start.left + start.width / 2);
    var dy = end.top + end.height / 2 - (start.top + start.height / 2);
    if (typeof flyer.animate !== 'function') {
      flyer.remove();
      pulseHeader();
      return;
    }
    var animation = flyer.animate(
      [
        { transform: 'translate3d(0,0,0) scale(.9)', opacity: 1 },
        {
          transform: 'translate3d(' + dx * 0.55 + 'px,' + (dy * 0.45 - 18) + 'px,0) scale(1.08)',
          opacity: 0.95,
          offset: 0.58,
        },
        { transform: 'translate3d(' + dx + 'px,' + dy + 'px,0) scale(.45)', opacity: 0.1 },
      ],
      { duration: 420, easing: 'cubic-bezier(.22,.8,.25,1)' }
    );
    animation.onfinish = function () {
      flyer.remove();
      pulseHeader();
    };
    animation.oncancel = function () {
      flyer.remove();
    };
  }

  function updateHeader(scope, records) {
    var header = headerControl();
    if (!header) return;
    var count = records.length;
    var badge = header.querySelector('[data-gtender-bookmark-count-val]');
    var label = header.querySelector('[data-gtender-header-bookmark-label]');
    var star = header.querySelector('[data-gtender-header-star] svg');
    var wording = t('list') + ', ' + count;
    if (scope.readFailed) {
      const message =
        locale() === 'zh'
          ? '收藏失敗，請稍後再試'
          : 'Bookmarks could not be read. Select to retry.';

      header.setAttribute('role', 'button');
      header.setAttribute('tabindex', '0');
      header.setAttribute('aria-haspopup', 'dialog');
      header.setAttribute('aria-label', message);

      if (badge) {
        badge.textContent = '';
        badge.hidden = true;
      }
      if (label) {
        label.textContent = message;
      }
      return;
    }

    header.setAttribute('role', 'button');
    header.setAttribute('tabindex', '0');
    header.setAttribute('aria-haspopup', 'dialog');
    header.setAttribute('aria-label', wording);
    header.style.cursor = 'pointer';
    header.style.minWidth = '44px';
    header.style.minHeight = '44px';
    header.style.justifyContent = 'center';
    header.removeAttribute('aria-live');
    header.removeAttribute('aria-atomic');

    if (badge) {
      var previous = badge.textContent;
      badge.textContent = String(count);
      badge.hidden = count === 0;
      badge.removeAttribute('aria-hidden');
      if (previous !== String(count) && !reducedMotion() && typeof badge.animate === 'function') {
        badge.animate(
          [{ transform: 'scale(.8)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }],
          { duration: 220 }
        );
      }
    }
    if (star) {
      star.setAttribute('fill', count > 0 ? 'currentColor' : 'none');
      star.setAttribute('stroke', count > 0 ? 'none' : 'currentColor');
    }
    if (label) {
      label.textContent = wording + ' · ' + (scope.authenticated ? t('account') : t('visitor'));
      label.setAttribute('aria-live', 'polite');
    }
  }

  function shareRecord(record, button, host) {
    var title = displayableTitle(record);
    var url = gtenderTenderUrl(record && record.id);
    if (!url || !title) return Promise.resolve();
    var message = shareMessage(title);
    var fallback = shareClipboardText(title, url);
    return Promise.resolve().then(async function () {
      try {
        if (typeof navigator.share === 'function') {
          await navigator.share({ title: 'GTender · ' + title, text: message, url: url });
          return;
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(fallback);
          const feedbackHost = host || (button && button.parentElement);

          if (feedbackHost) {
            let status = feedbackHost.querySelector('[data-gtender-share-status]');

            if (!status) {
              status = document.createElement('p');
              status.className = 'gtender-share-status';
              status.setAttribute('data-gtender-share-status', '');
              status.setAttribute('role', 'status');
              status.setAttribute('aria-live', 'polite');
              status.setAttribute('aria-atomic', 'true');
              feedbackHost.appendChild(status);
            }

            status.textContent = t('copied');
          }
          return;
        }
      } catch (error) {
        if (error && error.name === 'AbortError') return;
      }
      if (!host) return;
      var input = host.querySelector('[data-gtender-star-share-link]');
      if (!input) {
        input = document.createElement('textarea');
        input.readOnly = true;
        input.value = fallback;
        input.setAttribute('data-gtender-star-share-link', '');
        input.setAttribute('aria-label', t('copyLink'));
        host.appendChild(input);
      }
      input.focus();
      input.select();
    });
  }

  function dialogId(root) {
    return (root.id || 'gtender-public-entry') + '-starred-tenders';
  }

  function starDialogFocusableNodes(dialog) {
    return Array.prototype.filter.call(
      dialog.querySelectorAll(
        'a[href],button:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])'
      ),
      function (node) {
        return !node.hidden && node.getAttribute('aria-hidden') !== 'true';
      }
    );
  }

  function finalizeStarDialogClose(root) {
    var control = headerControl();
    if (control) control.setAttribute('aria-expanded', 'false');
    var opener = starDialogOpenerByRoot.get(root);
    starDialogOpenerByRoot.delete(root);
    if (opener && opener.isConnected && typeof opener.focus === 'function') {
      window.setTimeout(function () {
        try {
          opener.focus({ preventScroll: true });
        } catch (_error) {
          try {
            opener.focus();
          } catch (_) {}
        }
      }, 0);
    }
  }

  function closeStarDialog(root, dialog) {
    if (!dialog) return;
    try {
      if (typeof dialog.close === 'function' && dialog.open) dialog.close();
      else dialog.removeAttribute('open');
    } catch (_error) {
      dialog.removeAttribute('open');
    }
    finalizeStarDialogClose(root);
  }

  function ensureStarDialog(root) {
    var dialog = root.querySelector('[data-gtender-star-list-dialog]');
    if (dialog) return dialog;

    dialog = document.createElement('dialog');
    dialog.id = dialogId(root);
    dialog.className = 'gtender-filter-sheet';
    dialog.setAttribute('data-gtender-star-list-dialog', '');
    dialog.setAttribute('aria-labelledby', dialog.id + '-title');

    var shell = document.createElement('div');
    shell.className = 'gtender-filter-sheet__shell';
    var header = document.createElement('div');
    header.className = 'gtender-filter-sheet__header';
    var titles = document.createElement('div');
    var kicker = document.createElement('p');
    kicker.className = 'gtender-partner-entry__kicker';
    kicker.textContent = t('saved');
    var heading = document.createElement('h2');
    heading.id = dialog.id + '-title';
    heading.textContent = t('list');
    titles.appendChild(kicker);
    titles.appendChild(heading);
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'gtender-filter-sheet__close';
    close.setAttribute('aria-label', t('close'));
    close.textContent = '×';
    close.addEventListener('click', function () {
      closeStarDialog(root, dialog);
    });
    header.appendChild(titles);
    header.appendChild(close);

    var body = document.createElement('div');
    body.setAttribute('data-gtender-star-list', '');
    body.className = 'gtender-partner-entry__grid';
    body.style.maxHeight = 'min(68vh, 640px)';
    body.style.overflowY = 'auto';
    body.style.padding = '2px';

    shell.appendChild(header);
    shell.appendChild(body);
    dialog.appendChild(shell);
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) closeStarDialog(root, dialog);
    });
    dialog.addEventListener('cancel', function (event) {
      event.preventDefault();
      closeStarDialog(root, dialog);
    });
    dialog.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeStarDialog(root, dialog);
        return;
      }
      if (event.key !== 'Tab') return;
      var nodes = starDialogFocusableNodes(dialog);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
    dialog.addEventListener('close', function () {
      finalizeStarDialogClose(root);
    });
    root.appendChild(dialog);

    var control = headerControl();
    if (control) control.setAttribute('aria-controls', dialog.id);
    return dialog;
  }

  function renderStarList(root, scope, records) {
    var dialog = ensureStarDialog(root);
    var list = dialog.querySelector('[data-gtender-star-list]');
    list.replaceChildren();

    if (scope.readFailed) {
      var blocked = document.createElement('div');
      blocked.className = 'gtender-public-empty';
      blocked.setAttribute('role', 'status');
      var blockedTitle = document.createElement('h3');
      blockedTitle.textContent = t('storageUnavailable');
      var blockedHelp = document.createElement('p');
      blockedHelp.textContent = t('storageUnavailableHelp');
      var reset = document.createElement('button');
      reset.type = 'button';
      reset.className = 'gtender-public-empty__clear';
      reset.textContent = t('resetList');
      reset.addEventListener('click', function () {
        try {
          window.localStorage.removeItem(scope.key);
          syncRoot(root);
        } catch (_error) {}
      });
      blocked.appendChild(blockedTitle);
      blocked.appendChild(blockedHelp);
      blocked.appendChild(reset);
      list.appendChild(blocked);
      return;
    }

    if (!records.length) {
      var empty = document.createElement('div');
      empty.className = 'gtender-public-empty';
      var title = document.createElement('h3');
      title.textContent = t('empty');
      var help = document.createElement('p');
      help.textContent = t('emptyHelp');
      empty.appendChild(title);
      empty.appendChild(help);
      list.appendChild(empty);
      return;
    }

    records.forEach(function (record) {
      var card = document.createElement('article');
      var safeTitle = displayableTitle(record);
      var shareable = Boolean(safeTitle && gtenderTenderUrl(record.id));
      card.className = 'gtender-partner-entry__card';
      card.setAttribute('data-gtender-starred-item', record.id);
      card.setAttribute('data-gtender-star-record-state', safeTitle ? 'ready' : 'unavailable');

      var top = document.createElement('div');
      top.className = 'gtender-card__header-row';
      var badges = document.createElement('div');
      badges.className = 'gtender-card__badges';
      if (record.department) {
        var badge = document.createElement('span');
        badge.className = 'gtender-card__badge gtender-card__badge--department';
        badge.textContent = record.department;
        badges.appendChild(badge);
      }
      top.appendChild(badges);
      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'gtender-card-bookmark-btn';
      remove.setAttribute('data-gtender-star-list-remove', record.id);
      setButtonState(remove, true);
      remove.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        toggleStar(root, record.id, remove, false);
      });
      top.appendChild(remove);
      card.appendChild(top);

      var title = document.createElement('strong');
      title.className = 'gtender-card__title';
      title.textContent = safeTitle || t('unavailable');
      card.appendChild(title);
      if (record.deadline) {
        var deadline = document.createElement('p');
        deadline.className = 'gtender-card__date';
        deadline.textContent = record.deadline;
        card.appendChild(deadline);
      } else if (!safeTitle) {
        var unavailable = document.createElement('p');
        unavailable.className = 'gtender-public-record-meta';
        unavailable.textContent = t('unavailableHelp');
        card.appendChild(unavailable);
      }

      var actions = document.createElement('div');
      actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;align-items:center;';
      if (record.officialUrl) {
        var official = document.createElement('a');
        official.className = 'gtender-partner-entry__button';
        official.href = record.officialUrl;
        official.target = '_blank';
        official.rel = 'noopener noreferrer';
        official.textContent = t('official');
        actions.appendChild(official);
      }
      if (shareable) {
        var share = document.createElement('button');
        share.type = 'button';
        share.className = 'gtender-partner-entry__button';
        share.textContent = t('share');
        share.setAttribute('data-gtender-star-share', record.id);
        share.addEventListener('click', function () {
          void shareRecord(record, share, actions);
        });
        actions.appendChild(share);
      }
      if (actions.childNodes.length) card.appendChild(actions);
      list.appendChild(card);
    });
  }

  function openStarList(root) {
    var scope = sessionScope();
    var records = hydrateVisibleRecords(root, scope, readStars(scope));
    renderStarList(root, scope, records);
    var dialog = ensureStarDialog(root);
    var control = headerControl();
    if (control) {
      control.setAttribute('aria-expanded', 'true');
      const mobileMenuEntry = Boolean(
        control.closest('[data-gtender-mobile-tools-ready]') &&
        window.matchMedia('(max-width: 767px)').matches
      );

      const returnTarget = mobileMenuEntry
        ? document.querySelector('[data-sidebar-launcher="mobile"]')
        : control;

      starDialogOpenerByRoot.set(root, returnTarget || control);
    }
    if (!dialog.open) {
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch (_error) {
        dialog.setAttribute('open', '');
      }
    }
    var focusTarget =
      dialog.querySelector('.gtender-filter-sheet__close') || starDialogFocusableNodes(dialog)[0];
    if (focusTarget)
      window.setTimeout(function () {
        try {
          focusTarget.focus();
        } catch (_error) {}
      }, 0);
  }

  function bindHeader(root) {
    var header = headerControl();
    if (!header || header.getAttribute('data-gtender-star-list-bound') === 'true') return;
    header.setAttribute('data-gtender-star-list-bound', 'true');
    header.setAttribute('aria-expanded', 'false');
    header.addEventListener('click', function (event) {
      event.preventDefault();
      openStarList(root);
    });
    header.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      openStarList(root);
    });
  }

  function detailShareSource(root, id) {
    var card = findCard(root, id, null);
    return card && card.querySelector ? card.querySelector('[data-gtender-share]') : null;
  }

  function syncDetailActions(root, scope, records) {
    var id = clean(root.getAttribute('data-gtender-active-detail-id'));
    var star = root.querySelector('[data-gtender-detail-bookmark]');
    var share = root.querySelector('[data-gtender-detail-share]');
    if (!id) {
      if (star) star.hidden = true;
      if (share) share.hidden = true;
      return;
    }
    var saved = records.some(function (record) {
      return record.id === id;
    });
    if (star) {
      star.hidden = false;
      setButtonState(star, saved);
    }
    if (share) share.hidden = !SHARED_TENDER_ID_RE.test(id);
  }

  function bindDetailActions(root) {
    if (root.getAttribute('data-gtender-detail-actions-bound') === 'true') return;
    root.setAttribute('data-gtender-detail-actions-bound', 'true');
    var star = root.querySelector('[data-gtender-detail-bookmark]');
    var share = root.querySelector('[data-gtender-detail-share]');
    var dialog = root.querySelector('[data-gtender-public-detail]');

    root.addEventListener(
      'click',
      function (event) {
        var target =
          event.target && event.target.closest
            ? event.target.closest('[data-gtender-detail-open]')
            : null;
        if (!target || !root.contains(target)) return;
        var id = clean(target.getAttribute('data-gtender-detail-open'));
        if (!id) return;
        root.setAttribute('data-gtender-active-detail-id', id);
        var scope = sessionScope();
        syncDetailActions(root, scope, readStars(scope));
      },
      true
    );

    if (star) {
      star.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var id = clean(root.getAttribute('data-gtender-active-detail-id'));
        if (id) toggleStar(root, id, star, true);
      });
    }

    if (share) {
      share.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        var id = clean(root.getAttribute('data-gtender-active-detail-id'));
        if (!id) return;
        var source = detailShareSource(root, id);
        if (source) {
          source.dispatchEvent(
            new CustomEvent('gtender:share-request', {
              detail: {
                host: share.parentElement || dialog,
              },
            })
          );
          return;
        }
        var title = dialog && dialog.querySelector('[data-gtender-detail-subject]');
        var fallbackRecord = sanitizeRecord({
          id: id,
          title: title ? clean(title.textContent) : t('unavailable'),
        });
        if (fallbackRecord) void shareRecord(fallbackRecord, share, share.parentElement || dialog);
      });
    }

    if (dialog) {
      dialog.addEventListener('close', function () {
        root.removeAttribute('data-gtender-active-detail-id');
        if (star) star.hidden = true;
        if (share) share.hidden = true;
      });
    }
  }

  function syncButtons(root, scope, records) {
    var ids = new Set(
      records.map(function (record) {
        return record.id;
      })
    );
    root.querySelectorAll('[data-gtender-device-bookmark]').forEach(function (button) {
      setButtonState(button, ids.has(clean(button.getAttribute('data-gtender-device-bookmark'))));
    });
    updateHeader(scope, records);
    syncDetailActions(root, scope, records);
  }

  function toggleStar(root, id, sourceElement, allowFlight) {
    var key = clean(id);
    if (!key) return false;
    var scope = sessionScope();
    var records = readStars(scope);

    if (scope.readFailed) {
      openStarList(root);
      return false;
    }

    var index = records.findIndex(function (record) {
      return record.id === key;
    });
    var saved = index < 0;

    if (saved) {
      records.unshift(recordFromCard(root, key, sourceElement) || sanitizeRecord({ id: key }));
    } else {
      records.splice(index, 1);
    }
    if (!writeStars(scope, records)) return false;

    syncButtons(root, scope, records);
    var dialog = ensureStarDialog(root);
    if (dialog.open) renderStarList(root, scope, records);
    if (saved && allowFlight !== false) animateStarToHeader(sourceElement);
    else pulseHeader();

    try {
      document.dispatchEvent(
        new CustomEvent('gtender:bookmarks:changed', {
          detail: { id: key, saved: saved, count: records.length, scope: scope.scope },
        })
      );
    } catch (_error) {}
    try {
      if (typeof navigator.vibrate === 'function') navigator.vibrate(12);
    } catch (_error) {}
    return saved;
  }

  function createStarButton(root, id) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'gtender-card-bookmark-btn';
    button.setAttribute('data-gtender-device-bookmark', id);
    var scope = sessionScope();
    setButtonState(
      button,
      readStars(scope).some(function (record) {
        return record.id === id;
      })
    );
    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      toggleStar(root, id, button, true);
    });
    return button;
  }

  function projectCardShare(card) {
    if (!card || !card.querySelector) return;
    var share = card.querySelector('.gtender-card__action-row [data-gtender-share]');
    var header = card.querySelector('.gtender-card__header-row');
    var title = card.querySelector('.gtender-card__title');
    if (!share || !header) return;
    share.classList.add('gtender-card__share--visible');
    share.setAttribute('aria-label', t('share'));
    share.title = t('share');
    share.style.cssText =
      'position:absolute;top:8px;inset-inline-end:58px;width:44px;min-width:44px;height:44px;min-height:44px;padding:0;border-radius:50%;display:inline-grid;place-items:center;margin:0;';
    if (
      title &&
      window.matchMedia &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches
    ) {
      title.style.outline = '0';
      title.style.outlineOffset = '0';
      title.style.boxShadow = 'none';
    }
    share.innerHTML =
      '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12"/><path d="m7 8 5-5 5 5"/><path d="M5 13v6h14v-6"/></svg>';
    header.appendChild(share);
  }

  function enhanceCards(root) {
    var scope = sessionScope();
    var records = hydrateVisibleRecords(root, scope, readStars(scope));
    var ids = new Set(
      records.map(function (record) {
        return record.id;
      })
    );
    root.querySelectorAll('[data-gtender-detail-open]').forEach(function (detail) {
      var id = clean(detail.getAttribute('data-gtender-detail-open'));
      var card = detail.closest('.gtender-partner-entry__card, article');
      if (!id || !card) return;
      var star = card.querySelector('[data-gtender-device-bookmark]');
      if (!star) {
        star = createStarButton(root, id);
        var header = card.querySelector('.gtender-card__header-row');
        if (header) header.appendChild(star);
        else card.insertBefore(star, card.firstChild);
      }
      star.style.top = '8px';
      star.style.insetInlineEnd = '10px';
      setButtonState(star, ids.has(id));
      projectCardShare(card);
    });
    updateHeader(scope, records);
    syncDetailActions(root, scope, records);
  }

  function migrateLegacyVisitor(root) {
    var scope = sessionScope();
    if (scope.authenticated || readStars(scope).length) return;
    try {
      var legacy = JSON.parse(window.localStorage.getItem(LEGACY_BOOKMARK_KEY) || '[]');
      if (!Array.isArray(legacy) || !legacy.length) return;
      var records = legacy
        .map(function (id) {
          return recordFromCard(root, clean(id), null) || sanitizeRecord({ id: clean(id) });
        })
        .filter(Boolean);
      if (records.length && writeStars(scope, records))
        window.localStorage.removeItem(LEGACY_BOOKMARK_KEY);
    } catch (_error) {}
  }

  /** @param {DirectoryRoot} root */
  function syncRoot(root) {
    if (!root || !root.isConnected) return;
    migrateLegacyVisitor(root);
    ensureStarDialog(root);
    bindHeader(root);
    bindDetailActions(root);
    enhanceCards(root);
    var scope = sessionScope();
    var records = hydrateVisibleRecords(root, scope, readStars(scope));
    syncButtons(root, scope, records);
    var dialog = root.querySelector('[data-gtender-star-list-dialog]');
    if (dialog && dialog.open) renderStarList(root, scope, records);
  }

  function bind(root) {
    if (!root || roots.has(root)) return;
    roots.add(root);
    syncRoot(root);

    var timer = null;
    var observer = new MutationObserver(function () {
      if (timer) return;
      timer = setTimeout(function () {
        enhanceCards(root);
        timer = null;
      }, 80);
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function syncAllRoots() {
    roots.forEach(function (root) {
      if (root.isConnected) syncRoot(root);
      else roots.delete(root);
    });
  }

  function boot(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) bind(target);
    if (target.querySelectorAll) target.querySelectorAll(ROOT_SELECTOR).forEach(bind);
  }

  document.addEventListener('littleg:web-session-change', syncAllRoots);
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target || document);
  });
  window.addEventListener('storage', function (event) {
    if (
      event.key === null ||
      event.key === VISITOR_STAR_KEY ||
      String(event.key || '').indexOf(ACCOUNT_STAR_KEY_PREFIX) === 0 ||
      event.key === LEGACY_BOOKMARK_KEY
    )
      syncAllRoots();
  });

  if (document.readyState === 'loading')
    document.addEventListener(
      'DOMContentLoaded',
      function () {
        boot();
      },
      { once: true }
    );
  else boot();
})();
